import { findNode, getFlowOutputPorts } from './logicGraph.js';
import { LOGIC_NODE_SIZE } from './logicMigration.js';

export const LOGIC_PLACEMENT = Object.freeze({
    // Distance between the plate's right edge and the event column.
    plateGap: 40,
    // Distance between an event node and its action column.
    actionColumnOffset: 220,
    rowHeight: LOGIC_NODE_SIZE.height + 12,
});

const FLOW_TYPES = new Set(['condition', 'switch']);

const overlaps = (node, position) =>
    Math.abs(node.x - position.x) < LOGIC_NODE_SIZE.width &&
    Math.abs(node.y - position.y) < LOGIC_PLACEMENT.rowHeight;

/** First position at or below `position` that does not overlap an existing node. */
export function nextFreePosition(graph, position) {
    const candidate = { x: Math.round(position.x), y: Math.round(position.y) };
    while (graph.nodes.some(node => overlaps(node, candidate))) candidate.y += LOGIC_PLACEMENT.rowHeight;
    return candidate;
}

/**
 * Where a node added from the palette goes, and what it connects to.
 *
 * - An explicit `position` (a canvas drop) is used as is; otherwise the node
 *   goes to the next free slot of its column: GPIO entities below the plate,
 *   events beside the plate (or beside their GPIO node), actions beside their
 *   event.
 * - An event next to `anchorObject` ({ uuid, rect } in page coordinates) gets
 *   that object as its source. With `anchorNodeId` (the selected node) an
 *   event gets a selected GPIO node as its source, and an action, condition
 *   or switch is run by a selected event, condition (`then`) or switch (its
 *   first case).
 *
 * - A condition or switch next to `anchorObject` or a selected GPIO node
 *   compares its value (`inputPort: 'subject'`).
 * - A GPIO binding reads a selected GPIO node's state, or drives
 *   `anchorObject` from the left of the plate.
 *
 * @returns {{ position, connectFrom: object|null, connectTo?: object }} connectFrom
 *   feeds the new node's input; connectTo takes its output
 */
export function planPaletteNode(graph, entry, { position, anchorObject, anchorNodeId, plateWidth = 0, plateHeight = 0 } = {}) {
    const anchor = anchorNodeId ? findNode(graph, anchorNodeId) : null;
    if (entry.nodeType === 'gpioBinding') return planBindingNode(graph, { position, anchorObject, anchor });
    let connectFrom = null;
    if (entry.nodeType === 'event') {
        if (anchorObject) connectFrom = { object: anchorObject.uuid, port: 'events' };
        else if (anchor?.kind === 'gpio') connectFrom = { node: anchor.id, port: 'events' };
    } else if (FLOW_TYPES.has(entry.nodeType) && (anchorObject || anchor?.kind === 'gpio')) {
        // An object or GPIO entity is what a condition or switch compares.
        const subject = anchorObject ? { object: anchorObject.uuid, port: 'events' } : { node: anchor.id, port: 'state' };
        return { ...planPaletteNode(graph, entry, { position, plateWidth, plateHeight }), connectFrom: subject, inputPort: 'subject' };
    } else if (entry.nodeType !== 'gpio' && anchor) {
        const port = anchor.kind === 'event' ? 'actions' : getFlowOutputPorts(anchor)[0];
        if (port) connectFrom = { node: anchor.id, port };
    }
    if (position) return { position: { x: Math.round(position.x), y: Math.round(position.y) }, connectFrom };

    const eventColumn = plateWidth + LOGIC_PLACEMENT.plateGap;
    const besideAnchor = connectFrom?.node !== undefined
        ? { x: anchor.x + LOGIC_PLACEMENT.actionColumnOffset, y: anchor.y }
        : null;
    const preferred = entry.nodeType === 'gpio'
        ? { x: 0, y: plateHeight + LOGIC_PLACEMENT.plateGap }
        : besideAnchor ?? (entry.nodeType === 'event'
            ? { x: eventColumn, y: anchorObject?.rect.y ?? 0 }
            : { x: eventColumn + LOGIC_PLACEMENT.actionColumnOffset, y: 0 });
    return { position: nextFreePosition(graph, preferred), connectFrom };
}

function planBindingNode(graph, { position, anchorObject, anchor }) {
    const connectFrom = anchor?.kind === 'gpio' ? { node: anchor.id, port: 'state' } : null;
    const connectTo = !connectFrom && anchorObject ? { object: anchorObject.uuid, port: 'in' } : undefined;
    const preferred = position
        ?? (connectFrom ? { x: anchor.x + LOGIC_PLACEMENT.actionColumnOffset, y: anchor.y } : null)
        ?? { x: -(LOGIC_NODE_SIZE.width + LOGIC_PLACEMENT.plateGap), y: anchorObject?.rect?.y ?? 0 };
    const at = position ? { x: Math.round(position.x), y: Math.round(position.y) } : nextFreePosition(graph, preferred);
    return { position: at, connectFrom, ...(connectTo ? { connectTo } : {}) };
}
